import React, { useEffect } from 'react';
import {
  Activity,
  Shield,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FisioterapiaMusculoEsqueletica: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContactForm = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('formulario-contato');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const objetivos = [
    "Reduzir dor (aguda ou crônica)",
    "Restaurar mobilidade e amplitude de movimento",
    "Fortalecer músculos e melhorar a estabilidade articular",
    "Acelerar recuperação após lesões ou cirurgias ortopédicas",
    "Prevenir novas lesões por meio de exercícios e orientações posturais"
  ];

  const condicoesTratadas = [
    "Dores lombares e cervicais (lombalgia, cervicalgia)",
    "Hérnia de disco",
    "Tendinites e bursites",
    "Lesões esportivas (entorses, rupturas ligamentares, distensões musculares)",
    "Fraturas e cirurgias ortopédicas (como próteses de joelho ou quadril)",
    "Alterações posturais e sobrecargas por movimentos repetitivos"
  ];

  const recursos = [
    "Exercícios terapêuticos (fortalecimento, alongamento, estabilidade)",
    "Terapia manual (mobilizações, manipulações articulares, liberação miofascial)",
    "Eletroterapia (TENS, FES, ultrassom, laser)",
    "Orientações ergonômicas para o dia a dia e atividades físicas"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-500 to-primary-600 text-white pt-32 sm:pt-36 lg:pt-40 py-6 px-6 sm:px-8 sm:py-8 lg:py-10">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-4 sm:mb-6">
              Fisioterapia Músculo-Esquelética
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-primary-100 leading-relaxed">
              Afinal, do que se trata?
            </p>
          </div>
        </div>
      </section>

      {/* Definição */}
      <section className="py-6 px-6 sm:px-8 sm:py-8 lg:py-10">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 sm:mb-6 text-center">
              O que é?
            </h2> */}
            <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
              
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
                A fisioterapia musculo-esquelética (ou musculoesquelética) é uma área da fisioterapia <strong>com foco em</strong> diagnóstico, tratamento e prevenção de alterações nos músculos, ossos, articulações, tendões, ligamentos e nervos periféricos.
              </p>
              <div className="bg-primary-50 border-l-4 border-primary-500 p-4 sm:p-6">
                <p className="text-base sm:text-lg text-primary-700 font-medium">
                  👉 Em outras palavras, ela cuida do sistema responsável pelos movimentos e pela sustentação do corpo.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Objetivos */}
      <section className="py-6 px-6 sm:px-8 sm:py-8 lg:py-10 bg-blue-50">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 sm:mb-6 text-center">
              Principais Objetivos
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {objetivos.map((objetivo, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <p className="text-base text-gray-700 leading-relaxed">{objetivo}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Condições Tratadas */}
      <section className="py-6 px-6 sm:px-8 sm:py-8 lg:py-10">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 sm:mb-6 text-center">
              Exemplos de Condições Tratadas
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {condicoesTratadas.map((condicao, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow border-l-4 border-primary-500">
                  <div className="flex items-start space-x-3">
                    <Activity className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                    <p className="text-base text-gray-700 leading-relaxed font-medium">{condicao}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recursos Utilizados */}
      <section className="py-6 px-6 sm:px-8 sm:py-8 lg:py-10 bg-blue-50">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 sm:mb-6 text-center">
              Recursos Utilizados
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recursos.map((recurso, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                    <p className="text-base text-gray-700 leading-relaxed">{recurso}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-6 px-6 sm:px-8 sm:py-8 lg:py-10">
        <div className="container-custom">
          <div className="text-center bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 sm:p-8 lg:p-10 text-white">
            <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-bold mb-4">
              Pronto para iniciar seu tratamento?
            </h3>
            <p className="text-sm sm:text-base text-primary-100 mb-6 max-w-2xl mx-auto leading-relaxed">
              Não deixe a dor limitar sua qualidade de vida. Entre em contato e agende sua avaliação.
            </p>
            <button
              onClick={scrollToContactForm}
              className="bg-white text-primary-500 font-semibold py-4 px-8 rounded-lg hover:bg-gray-100 transition-colors duration-300 inline-flex items-center space-x-2 text-sm sm:text-base min-h-[48px]"
            >
              <span>Entrar em Contato</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FisioterapiaMusculoEsqueletica;